import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Globe2, ArrowLeft } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '../../i18n';

/**
 * Compact footer for the public login/auth/onboarding entry views.
 *
 * Provides (1) a small secondary "back to homepage" link so public flows are
 * never a dead end, and (2) a language switcher (native <select> for a11y +
 * zero layout cost). Navigation only — no auth/session side effects.
 */
export default function LoginLanguageSwitcher({ className = '' }) {
  const { t, i18n } = useTranslation('common');
  const current = SUPPORTED_LANGUAGES.includes(i18n.language) ? i18n.language : 'sv';

  const onChange = (e) => {
    const next = e.target.value;
    if (next !== current) i18n.changeLanguage(next);
  };

  return (
    <div className={`flex flex-col items-center gap-3 ${className}`}>
      <Link
        to="/welcome"
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        data-testid="back-to-homepage-link"
      >
        <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
        {t('actions.back_to_home')}
      </Link>

      <div
        className="flex items-center justify-center gap-2 text-xs text-muted-foreground"
        data-testid="login-language-switcher"
      >
        <Globe2 className="w-3.5 h-3.5" aria-hidden="true" />
        <label htmlFor="login-language-select" className="sr-only">
          {t('language.aria')}
        </label>
        <select
          id="login-language-select"
          value={current}
          onChange={onChange}
          aria-label={t('language.aria')}
          data-testid="login-language-select"
          className="bg-transparent border border-border rounded-md px-2 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40 cursor-pointer"
        >
          {SUPPORTED_LANGUAGES.map((code) => (
            <option key={code} value={code}>
              {t(`language.${code}`)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
